import {
  Card,
  Stack,
  Table,
  Button,
  Container,
  TableBody,
  Typography,
  TableContainer,
} from '@mui/material';

import { useState, useEffect, useCallback } from 'react';
import { useBoolean } from 'src/hooks/use-boolean';

import { Pagination } from 'src/type/utils';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import { fParams, ParamsType } from 'src/utils/format-params';
import usePagination from 'src/hooks/use-pagination';
import Scrollbar from 'src/components/scrollbar';
import TableEmptyRows from 'src/components/table/table-empty-rows';
import TableHeadCustom from 'src/components/table/table-head-custom';
import TableLoading from 'src/components/table/table-loading';
import TableNoData from 'src/components/table/table-no-data';
import TablePaginationCustom from 'src/components/table/table-pagination-custom';
import { emptyRows } from 'src/components/table/utils';
import Iconify from 'src/components/iconify';
import { ProductType } from 'src/type/product';
import ProductTableRow from '../product-table-row';
import ProductFormDialog from '../product-form-dialog';

const tableHead = [
  { id: 'invoiceNumber', label: 'สินค้า' },
  { id: 'status', label: 'สถานะ', width: 120 },
  { id: 'sequence', label: 'ลำดับ', width: 100, align: 'center' },
  { id: 'action', width: 60 },
];

export default function ProductView() {
  const initialize = useBoolean(true);
  const reload = useBoolean(false);
  const form = useBoolean(false);
  const loading = useBoolean();
  const [edit, setEdit] = useState<ProductType | null>(null);

  const {
    currentPage,
    prevPage,
    rowsPerPage,
    meta,
    onSetData,
    onResetPage,
    onChangePage,
    onChangeRowsPerPage,
    data: tableData,
  } = usePagination<ProductType>({ defaultRowsPerPage: 5 });

  useEffect(() => {
    const pageDirection = currentPage > prevPage ? 'next' : 'prev';
    const params = {
      limit: rowsPerPage,
      ...(pageDirection === 'next' && { lastDocId: meta.lastDocId }),
      ...(pageDirection === 'prev' && { firstDocId: meta.firstDocId }),
    };
    handleGetTx(params);
  }, [currentPage, rowsPerPage, reload.value]);

  useEffect(() => {
    if (!form.value) {
      setEdit(null);
    }
  }, [form.value]);

  const handleGetTx = useCallback(async (params: ParamsType) => {
    try {
      initialize.onTrue();
      const { data } = await axios.get<Pagination<ProductType>>(endpoints.product.get, {
        params: fParams(params),
      });
      onSetData(data);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  }, []);

  const handleReload = useCallback(() => {
    onResetPage();
    reload.onToggle();
  }, []);

  const handleEdit = useCallback(async (product: ProductType) => {
    setEdit(product);
    form.onTrue();
  }, []);

  const handleDelete = useCallback(async (product: ProductType) => {
    try {
      loading.onTrue();
      await axios.delete(endpoints.product.delete(product.id));
      handleReload();
    } catch (error) {
      toastError(error);
    } finally {
      loading.onFalse();
    }
  }, []);

  const handleMoveUp = useCallback(async (product: ProductType) => {
    try {
      loading.onTrue();
      await axios.patch(endpoints.product.updateSequence(product.id), {
        direction: 'up',
      });
      handleReload();
    } catch (error) {
      toastError(error);
    } finally {
      loading.onFalse();
    }
  }, []);

  const handleMoveDown = useCallback(async (product: ProductType) => {
    try {
      loading.onTrue();
      await axios.patch(endpoints.product.updateSequence(product.id), {
        direction: 'down',
      });
      handleReload();
    } catch (error) {
      toastError(error);
    } finally {
      loading.onFalse();
    }
  }, []);

  return (
    <>
      <Container>
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h4" gutterBottom>
              สินค้า
            </Typography>
            <Button
              onClick={form.onTrue}
              variant="contained"
              startIcon={<Iconify icon="mingcute:add-line" />}
            >
              สร้างสินค้า
            </Button>
          </Stack>

          <Card>
            <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
              <Scrollbar>
                <Table sx={{ minWidth: 800 }}>
                  <TableHeadCustom headLabel={tableHead} />

                  <TableBody>
                    <TableLoading loading={initialize.value} rows={rowsPerPage}>
                      {tableData.map((row, index) => (
                        <ProductTableRow
                          key={row.id}
                          row={row}
                          loading={loading.value}
                          onEdit={() => handleEdit(row)}
                          onDelete={() => handleDelete(row)}
                          onMoveUp={() => handleMoveUp(row)}
                          onMoveDown={() => handleMoveDown(row)}
                          isFirst={index === 0}
                          isLast={index === tableData.length - 1}
                        />
                      ))}

                      <TableEmptyRows
                        show={!initialize.value}
                        height={76}
                        emptyRows={emptyRows(currentPage, rowsPerPage, meta.totalResults)}
                      />

                      <TableNoData notFound={!tableData.length} />
                    </TableLoading>
                  </TableBody>
                </Table>
              </Scrollbar>
            </TableContainer>

            <TablePaginationCustom
              count={meta.totalResults}
              page={currentPage}
              rowsPerPage={rowsPerPage}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />
          </Card>
        </Stack>
      </Container>

      <ProductFormDialog
        open={form.value}
        onClose={form.onFalse}
        handleReload={handleReload}
        product={edit}
      />
    </>
  );
}
